/**
 * Automatic Bet Processing
 * Processes bets when fight results change (template propagation)
 */

import type { Event, Fight } from '../types';
import { processFightResult } from './result';
import { saveEvent } from './persistence';

/**
 * Process bets for all fights that have new or changed results
 * Compares old and new fight data to detect result changes
 * 
 * @param oldEvent - Previous event state
 * @param newEvent - New event state (with updated fights from template)
 * @returns Updated event with processed bets
 */
export async function autoProcessNewResults(
  oldEvent: Event,
  newEvent: Event
): Promise<Event> {
  console.log('='.repeat(80));
  console.log('[AutoProcessResults] START');
  console.log('[AutoProcessResults] Event ID:', newEvent.id);
  console.log('[AutoProcessResults] Is Template?', newEvent.isTemplate);
  console.log('[AutoProcessResults] Participants:', newEvent.participants.length);
  console.log('[AutoProcessResults] Old Fights:', oldEvent.fights.length);
  console.log('[AutoProcessResults] New Fights:', newEvent.fights.length);
  
  // Only process for event instances (not templates)
  if (newEvent.isTemplate) {
    console.log('[AutoProcessResults] SKIP: Is a template');
    return newEvent;
  }

  // No participants = no bets to process
  if (newEvent.participants.length === 0) {
    console.log('[AutoProcessResults] SKIP: No participants');
    return newEvent;
  }

  // Count total bets before processing
  const totalBets = newEvent.participants.flatMap(p => p.bets).length;
  const pendingBets = newEvent.participants.flatMap(p => p.bets).filter(b => b.status === 'pending').length;
  console.log('[AutoProcessResults] Total Bets:', totalBets);
  console.log('[AutoProcessResults] Pending Bets:', pendingBets);

  // Find fights with new or changed results
  const fightsToProcess: Fight[] = [];
  
  newEvent.fights.forEach((newFight) => {
    const oldFight = oldEvent.fights.find(f => f.id === newFight.id);
    
    console.log(`[AutoProcessResults] Checking fight ${newFight.id}:`, {
      oldResult: oldFight?.result ? 'YES' : 'NO',
      newResult: newFight.result ? 'YES' : 'NO',
      oldStatus: oldFight?.status,
      newStatus: newFight.status,
    });
    
    // Check if this fight has a new result or a changed result
    const hasNewResult = !oldFight?.result && newFight.result;
    const hasChangedResult = oldFight?.result && newFight.result && 
                             JSON.stringify(oldFight.result) !== JSON.stringify(newFight.result);
    
    if (hasNewResult || hasChangedResult) {
      console.log(`[AutoProcessResults] ✅ WILL PROCESS: ${hasNewResult ? 'NEW' : 'CHANGED'} result for fight:`, newFight.id);
      fightsToProcess.push(newFight);
    }
  });

  // If no results to process, return unchanged
  if (fightsToProcess.length === 0) {
    console.log('[AutoProcessResults] NO FIGHTS TO PROCESS - returning unchanged');
    console.log('='.repeat(80));
    return newEvent;
  }
  
  console.log('[AutoProcessResults] FIGHTS TO PROCESS:', fightsToProcess.length);

  // Process each fight with new/changed results
  let updatedEvent = { ...newEvent };
  
  for (const fight of fightsToProcess) {
    if (!fight.result) continue;
    
    console.log(`[AutoProcessResults] Processing ${updatedEvent.participants.length} participants' bets for fight ${fight.id}`);
    
    // Get current bets from participants
    const allBets = updatedEvent.participants.flatMap(p => p.bets);
    
    // Process bet outcomes for this fight
    const { updatedParticipants, updatedBets } = processFightResult(
      fight,
      allBets,
      updatedEvent.participants
    );
    
    // Update event with new participant/bet data
    updatedEvent = {
      ...updatedEvent,
      participants: updatedParticipants,
    };
    
    const processedBetsForFight = updatedBets.filter(b => b.fightId === fight.id);
    const wonCount = processedBetsForFight.filter(b => b.status === 'won').length;
    const lostCount = processedBetsForFight.filter(b => b.status === 'lost').length;
    const stillPending = processedBetsForFight.filter(b => b.status === 'pending').length;
    
    console.log(`[AutoProcessResults] Processed ${processedBetsForFight.length} bets for fight ${fight.id}:`, {
      won: wonCount,
      lost: lostCount,
      pending: stillPending
    });
  }

  // Count bets after processing
  const finalPendingBets = updatedEvent.participants.flatMap(p => p.bets).filter(b => b.status === 'pending').length;
  console.log('[AutoProcessResults] Pending Bets After Processing:', finalPendingBets);

  // Save updated event to Firestore
  try {
    console.log('[AutoProcessResults] Saving event to Firestore...');
    await saveEvent(updatedEvent);
    console.log(`[AutoProcessResults] ✅ SAVED event with processed bets for ${fightsToProcess.length} fight(s)`);
  } catch (error) {
    console.error('[AutoProcessResults] ❌ FAILED to save event:', error);
    // Don't throw - return updated event anyway so UI updates
  }

  console.log('[AutoProcessResults] END');
  console.log('='.repeat(80));
  return updatedEvent;
}




